import React, { useCallback, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { SeasonProvider } from '../context/SeasonContext';
import { useClubPermissions } from '../hooks/useClubPermissions';
import { BackButton } from '../components/common';
import { Heading, Text } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import Button from '../components/common/Button';
import ClubLogo from '../components/clubs/ClubLogo';
import SessionsPlanningPanel from '../components/sessions/hub/SessionsPlanningPanel';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../utils/sessionMobileLayout';

const PAGE_SHELL = `${APP_PAGE_BLEED_ROOT} min-h-screen bg-background`;
const PAGE_INNER = `mx-auto max-w-app-shell ${APP_PAGE_GUTTER} pt-3 pb-8 md:pt-6`;

const ClubSessionPlanning = ({ currentUser }) => {
  const { clubId, blockId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [blockTitle, setBlockTitle] = useState(null);

  const {
    club: selectedClub,
    role: clubRole,
    loading: clubLoading,
    hasPermission,
    error: clubError,
  } = useClubPermissions(clubId, currentUser);

  const isAdmin =
    currentUser?.role === 'admin' ||
    currentUser?.role === 'super_admin' ||
    clubRole === 'host' ||
    clubRole === 'admin' ||
    clubRole === 'owner';

  const fromPlanSessions = Boolean(location.state?.fromPlanSessions);
  const setupPath = `/club/${clubId}/sessions/setup`;
  const handleBack = () => {
    if (blockId) {
      navigate(setupPath, { state: location.state });
      return;
    }
    if (fromPlanSessions) {
      navigate(`/club/${clubId}/sessions`, { state: location.state });
      return;
    }
    navigate(`/club-management/${clubId}`, { state: location.state });
  };

  const handleBlockLoaded = useCallback((block) => {
    setBlockTitle(block?.block_name || null);
  }, []);

  const handleBlockRemoved = useCallback(() => {
    navigate(setupPath, { state: location.state });
  }, [navigate, setupPath, location.state]);

  const backLabel = blockId
    ? 'Back to Block bookings'
    : fromPlanSessions
      ? 'Back to Plan sessions'
      : 'Back to Management';

  if (clubLoading) {
    return (
      <div className={PAGE_SHELL}>
        <div className={PAGE_INNER}>
          <Text className="text-text-secondary">Loading…</Text>
        </div>
      </div>
    );
  }

  if (clubError || !hasPermission || !isAdmin) {
    return (
      <div className={PAGE_SHELL}>
        <div className={PAGE_INNER}>
          <Card variant="default" size="medium" className="py-12 text-center">
            <Heading level={2} className="mb-4">Access denied</Heading>
            <Text className="mb-6 text-text-secondary">
              {clubError || 'You do not have permission to manage block bookings for this club.'}
            </Text>
            <Button onClick={handleBack} variant="primary">
              {backLabel}
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <SeasonProvider clubId={clubId}>
      <div className={PAGE_SHELL}>
        <div className={PAGE_INNER}>
          <div className="mb-6 sm:mb-8">
            <BackButton
              hideOnDesktop
              className="mb-2"
              onClick={handleBack}
            >
              {backLabel}
            </BackButton>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-3">
                {selectedClub ? (
                  <ClubLogo
                    logoImageUrl={selectedClub.logo_image_url}
                    clubName={selectedClub.name}
                    size="sm"
                    className="border border-border-medium shadow-brand-sm"
                  />
                ) : null}
                <Heading level={1} className="min-w-0 truncate text-xl font-bold text-text-primary sm:text-2xl">
                  {blockId ? (blockTitle || 'Block booking') : 'Block bookings, series, and templates'}
                </Heading>
              </div>
              {blockId ? null : (
                <Text className="mt-1 text-sm text-text-secondary">
                  Recurring blocks, standing series, and saved templates for {selectedClub?.name}.
                </Text>
              )}
            </div>
          </div>

          <SessionsPlanningPanel
            clubId={clubId}
            blockId={blockId}
            selectedClub={selectedClub}
            currentUser={currentUser}
            onBlockLoaded={handleBlockLoaded}
            onBlockRemoved={handleBlockRemoved}
          />
        </div>
      </div>
    </SeasonProvider>
  );
};

export default ClubSessionPlanning;
